import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Extension } from '@tiptap/core';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Link2,
  Type,
  Undo,
  Redo,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  availableVariables?: string[];
  onInsertVariable?: (variable: string) => void;
}

// Custom extension to handle Shift+Enter for single line breaks
const ShiftEnterExtension = Extension.create({
  name: 'shiftEnter',

  addKeyboardShortcuts() {
    return {
      'Shift-Enter': () => {
        this.editor.commands.setHardBreak();
        return true;
      },
    };
  },
});

export function RichTextEditor({
  content,
  onChange,
  placeholder = 'Write your email content here...',
  availableVariables = [],
  onInsertVariable,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        hardBreak: {
          keepMarks: true,
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      ShiftEnterExtension,
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'focus:outline-none',
      },
    },
  });

  // Update editor content when prop changes
  useEffect(() => {
    if (editor && content !== undefined && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const insertVariable = (variable: string) => {
    editor.chain().focus().insertContent(variable).run();
    if (onInsertVariable) {
      onInsertVariable(variable);
    }
  };

  const addButton = () => {
    const text = window.prompt('Button text:');
    const url = window.prompt('Button URL:');
    if (text && url) {
      editor
        .chain()
        .focus()
        .insertContent(
          `<a href="${url}" style="display: inline-block; padding: 10px 20px; background-color: #7c3aed; color: white; text-decoration: none; border-radius: 6px; margin: 10px 0;">${text}</a>`
        )
        .run();
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="border-b bg-gray-50 p-2 flex flex-wrap gap-1">
        <div className="flex gap-1 pr-2 border-r">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`transition-colors ${editor.isActive('bold') ? 'bg-purple-100 hover:bg-purple-200 text-purple-700' : 'hover:bg-gray-100'}`}
            title="Bold (Ctrl+B)"
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`transition-colors ${editor.isActive('italic') ? 'bg-purple-100 hover:bg-purple-200 text-purple-700' : 'hover:bg-gray-100'}`}
            title="Italic (Ctrl+I)"
          >
            <Italic className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex gap-1 pr-2 border-r">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            className={`transition-colors ${editor.isActive('heading', { level: 2 }) ? 'bg-purple-100 hover:bg-purple-200 text-purple-700' : 'hover:bg-gray-100'}`}
            title="Heading"
          >
            <Type className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex gap-1 pr-2 border-r">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`transition-colors ${editor.isActive('bulletList') ? 'bg-purple-100 hover:bg-purple-200 text-purple-700' : 'hover:bg-gray-100'}`}
            title="Bullet List"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`transition-colors ${editor.isActive('orderedList') ? 'bg-purple-100 hover:bg-purple-200 text-purple-700' : 'hover:bg-gray-100'}`}
            title="Numbered List"
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex gap-1 pr-2 border-r">
          <Button type="button" variant="ghost" size="sm" onClick={addLink}>
            <Link2 className="h-4 w-4" />
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={addButton}>
            + Button
          </Button>
        </div>

        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <Undo className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <Redo className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Variable insertion */}
      {availableVariables.length > 0 && (
        <div className="border-b bg-gray-50 px-3 py-2 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-gray-600">Insert variable:</span>
          {availableVariables.map((variable) => (
            <button
              key={variable}
              type="button"
              onClick={() => insertVariable(variable)}
              className="px-3 py-1 text-sm bg-primary/10 hover:bg-primary/20 text-primary rounded-full transition-colors"
            >
              + {variable.replace('{{', '').replace('}}', '').replace('_', ' ')}
            </button>
          ))}
        </div>
      )}

      {/* Editor */}
      <EditorContent
        editor={editor}
        className="p-4 min-h-[300px] focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror_p]:mb-2 [&_.ProseMirror_p:last-child]:mb-0 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:ml-6 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:ml-6 [&_.ProseMirror_li]:mb-1"
      />

      {/* Helper text */}
      <div className="border-t bg-gray-50 px-3 py-2">
        <p className="text-xs text-gray-500">
          Tip: Press Enter for new paragraph, Shift+Enter for line break
        </p>
      </div>
    </div>
  );
}
